import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Contact, Handle } from "../../../types/contact.ts";
import type {
  ContactAutocompleteDropdownRef,
  ContactDropdownPosition,
} from "../../ui/contact-autocomplete.tsx";

/**
 * Text after "@" that still reads as a mention query: words separated by single
 * spaces, so "chris hat" can narrow by last name. A leading space, a double space,
 * or a line break ends it. Each group must end in a space, so matching is linear.
 */
const MENTION_QUERY = /^(\S+ )*\S*$/;

/** Mirrors the server's MIN_PREFIX_TERM_LENGTH: shorter words match only exactly. */
const MIN_PREFIX_WORD_LENGTH = 2;

interface UseMentionSystemOptions {
  value: string;
  onChange: (value: string) => void;
  getDropdownPosition: (charOffset?: number, text?: string) => ContactDropdownPosition | null;
  cursorTimeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  isUserInputRef: React.MutableRefObject<boolean>;
}

export function useMentionSystem({
  value,
  onChange,
  getDropdownPosition,
  cursorTimeoutRef,
  textareaRef,
  isUserInputRef,
}: UseMentionSystemOptions) {
  // ─── State ────────────────────────────────────────────────────────────
  const [mentionQuery, setMentionQuery] = useState<string>("");
  const [mentionStartPos, setMentionStartPos] = useState<number | null>(null);
  const [mentionDropdownPos, setMentionDropdownPos] = useState<ContactDropdownPosition | null>(
    null,
  );

  const [resolvedMentions, setResolvedMentions] = useState<
    Array<{ text: string; start: number; contactId?: string }>
  >([]);
  const [editingMentionIndex, setEditingMentionIndex] = useState<number | null>(null);
  const editingMentionRef = useRef<{ text: string; start: number; contactId?: string } | null>(
    null,
  );
  const dropdownRef = useRef<ContactAutocompleteDropdownRef>(null);
  // The "@" the user is done with (Escape, or a multi-word query that matched
  // nobody), plus the query it carried. Spaces are allowed in a query, so without
  // this the menu would reopen on every keystroke of the sentence typed after a
  // name. The dismissal only holds while that query keeps growing.
  const dismissedRef = useRef<{ at: number; query: string } | null>(null);

  // ─── Close helper ─────────────────────────────────────────────────────
  const close = useCallback(() => {
    setMentionQuery("");
    setMentionStartPos(null);
    setMentionDropdownPos(null);
    setEditingMentionIndex(null);
    editingMentionRef.current = null;
  }, []);

  // ─── Derived state ───────────────────────────────────────────────────
  const isOpen = Boolean(mentionQuery) || editingMentionIndex !== null;
  const hasQueryOverlay = mentionStartPos !== null && Boolean(mentionQuery);

  // ─── Input detection ─────────────────────────────────────────────────
  /** Returns true if input was consumed by mention detection. */
  const onInputChange = useCallback(
    (textBeforeCursor: string): boolean => {
      const lastAtIndex = textBeforeCursor.lastIndexOf("@");
      const textAfterAt = lastAtIndex === -1 ? "" : textBeforeCursor.slice(lastAtIndex + 1);

      // Editing the query — a backspace, say — can make it match again, so a
      // dismissal survives only the keystrokes that extend it.
      const dismissed = dismissedRef.current;
      const stillDismissed =
        dismissed !== null &&
        dismissed.at === lastAtIndex &&
        textAfterAt.startsWith(dismissed.query);
      if (!stillDismissed) dismissedRef.current = null;

      if (lastAtIndex !== -1 && !stillDismissed) {
        const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : "";
        const isAtWordStart = lastAtIndex === 0 || /\s/.test(charBeforeAt);

        if (isAtWordStart && MENTION_QUERY.test(textAfterAt)) {
          setMentionQuery(textAfterAt);
          setMentionStartPos(lastAtIndex);
          setMentionDropdownPos(getDropdownPosition());
          return true;
        }
      }

      // No active mention
      close();
      return false;
    },
    [getDropdownPosition, close],
  );

  // ─── Keyboard handling ───────────────────────────────────────────────
  /** Returns true if the key event was handled. */
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent): boolean => {
      if (!isOpen || !dropdownRef.current) return false;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        dropdownRef.current.handleArrowDown();
        return true;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        dropdownRef.current.handleArrowUp();
        return true;
      }
      if (e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) {
        e.preventDefault();
        const wasSelected = dropdownRef.current.handleEnter();
        if (wasSelected) return true;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        if (mentionStartPos !== null)
          dismissedRef.current = { at: mentionStartPos, query: mentionQuery };
        close();
        return true;
      }
      return false;
    },
    [isOpen, close, mentionStartPos, mentionQuery],
  );

  /**
   * Once a multi-word query matches nobody, the user is writing prose, not a name.
   *
   * A one-letter last word is exempt: the server matches it exactly rather than as
   * a prefix, so "chris h" misses Chris Hatfield, and a dismissal here would keep
   * the menu closed through "chris ha", which does match.
   */
  const handleNoMatches = useCallback(() => {
    if (mentionStartPos === null || !mentionQuery.includes(" ")) return;
    const lastWord = mentionQuery.slice(mentionQuery.lastIndexOf(" ") + 1);
    if (lastWord.length < MIN_PREFIX_WORD_LENGTH) return;
    dismissedRef.current = { at: mentionStartPos, query: mentionQuery };
    close();
  }, [mentionStartPos, mentionQuery, close]);

  // ─── Mention insertion ───────────────────────────────────────────────
  const insertMention = useCallback(
    (mentionText: string, contactId?: string) => {
      isUserInputRef.current = true;

      const editing = editingMentionRef.current;

      if (editing) {
        const oldStart = editing.start;
        const oldEnd = oldStart + editing.text.length;
        const beforeMention = value.slice(0, oldStart);
        let afterMention = value.slice(oldEnd);
        afterMention = afterMention.trimStart();

        const newValue =
          afterMention.length > 0
            ? `${beforeMention}${mentionText} ${afterMention}`
            : `${beforeMention}${mentionText} `;

        const lengthDelta = newValue.length - value.length;
        setResolvedMentions((prev) => {
          const updated: typeof prev = [];
          for (let i = 0; i < prev.length; i++) {
            if (i === editingMentionIndex) {
              updated.push({ text: mentionText, start: oldStart, contactId });
            } else if (prev[i].start > oldStart) {
              updated.push({ ...prev[i], start: prev[i].start + lengthDelta });
            } else {
              updated.push(prev[i]);
            }
          }
          return updated;
        });

        onChange(newValue);
        close();

        if (textareaRef.current) {
          const newCursorPos = beforeMention.length + mentionText.length + 1;
          if (cursorTimeoutRef.current) clearTimeout(cursorTimeoutRef.current);
          cursorTimeoutRef.current = setTimeout(() => {
            textareaRef.current?.focus();
            textareaRef.current?.setSelectionRange(newCursorPos, newCursorPos);
          }, 0);
        }
        return;
      }

      if (mentionStartPos === null) return;

      const AT_SYMBOL_LENGTH = 1;
      // The query may span several words; past it, also swallow the rest of a word
      // the cursor was inside of ("@chr|is"). A query ending in a space ends between
      // words, so the word after it is the user's text, not part of the name.
      const queryEndPos = mentionStartPos + AT_SYMBOL_LENGTH + mentionQuery.length;
      const restOfWord = mentionQuery.endsWith(" ")
        ? ""
        : (value.slice(queryEndPos).match(/^\S*/)?.[0] ?? "");
      const mentionEndPos = queryEndPos + restOfWord.length;

      const beforeMention = value.slice(0, mentionStartPos);
      let afterMention = value.slice(mentionEndPos);
      afterMention = afterMention.trimStart();

      const newValue =
        afterMention.length > 0
          ? `${beforeMention}${mentionText} ${afterMention}`
          : `${beforeMention}${mentionText} `;

      const lengthDelta = newValue.length - value.length;
      setResolvedMentions((prev) => {
        const adjusted = prev.map((m) =>
          m.start > mentionStartPos ? { ...m, start: m.start + lengthDelta } : m,
        );
        return [...adjusted, { text: mentionText, start: mentionStartPos, contactId }];
      });

      onChange(newValue);
      close();

      if (textareaRef.current) {
        const newCursorPos = beforeMention.length + mentionText.length + 1;
        if (cursorTimeoutRef.current) clearTimeout(cursorTimeoutRef.current);
        cursorTimeoutRef.current = setTimeout(() => {
          textareaRef.current?.focus();
          textareaRef.current?.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
      }
    },
    [
      value,
      mentionStartPos,
      mentionQuery,
      editingMentionIndex,
      onChange,
      close,
      textareaRef,
      cursorTimeoutRef,
      isUserInputRef,
    ],
  );

  const handleContactSelect = useCallback(
    (contact: Contact) => {
      const emailHandle = contact.handles?.find((h) => h.type === "emailAddress");
      const phoneHandle = contact.handles?.find((h) => h.type === "phoneNumber");
      const handle = emailHandle || phoneHandle;

      const formatMentionText = (): string => {
        const hasName = Boolean(contact.name);
        const hasHandle = Boolean(handle?.value);

        if (hasName && hasHandle) {
          return `${contact.name} (${handle?.value})`;
        }
        if (hasHandle) {
          return handle?.value ?? "";
        }
        return contact.name ?? "";
      };

      insertMention(formatMentionText(), contact.id);
    },
    [insertMention],
  );

  const handleHandleSelect = useCallback(
    (contact: Contact, handle: Handle) => {
      const hasName = Boolean(contact.name);
      const hasHandle = Boolean(handle.value);
      let text: string;
      if (hasName && hasHandle) {
        text = `${contact.name} (${handle.value})`;
      } else if (hasHandle) {
        text = handle.value;
      } else {
        text = contact.name ?? "";
      }
      insertMention(text, contact.id);
    },
    [insertMention],
  );

  const handleEmailSelect = useCallback(
    (email: string) => {
      insertMention(email);
    },
    [insertMention],
  );

  // ─── Overlay segments ────────────────────────────────────────────────
  const overlaySegments = useMemo(() => {
    if (resolvedMentions.length === 0) return [];

    const validMentions = resolvedMentions.filter(
      (m) =>
        value.length >= m.start + m.text.length &&
        value.slice(m.start, m.start + m.text.length) === m.text,
    );

    if (validMentions.length === 0) return [];

    const sorted = [...validMentions].sort((a, b) => a.start - b.start);

    const result: Array<{ text: string; mentionIndex?: number }> = [];
    let cursor = 0;
    for (const mention of sorted) {
      const idx = resolvedMentions.indexOf(mention);
      if (cursor < mention.start) {
        result.push({ text: value.slice(cursor, mention.start) });
      }
      result.push({
        text: value.slice(mention.start, mention.start + mention.text.length),
        mentionIndex: idx,
      });
      cursor = mention.start + mention.text.length;
    }
    if (cursor < value.length) {
      result.push({ text: value.slice(cursor) });
    }

    return result;
  }, [resolvedMentions, value]);

  const hasLinkOverlay =
    overlaySegments.length > 0 && overlaySegments.some((s) => s.mentionIndex !== undefined);

  // ─── Segment click ───────────────────────────────────────────────────
  const handleSegmentClick = useCallback(
    (mentionIdx: number) => {
      const mention = resolvedMentions[mentionIdx];
      if (!mention) return;

      editingMentionRef.current = mention;
      setEditingMentionIndex(mentionIdx);
      setMentionQuery("");
      setMentionStartPos(null);
      setMentionDropdownPos(getDropdownPosition(mention.start));
    },
    [resolvedMentions, getDropdownPosition],
  );

  // ─── Effects ─────────────────────────────────────────────────────────
  // Clear resolved mentions when text is fully cleared
  useEffect(() => {
    if (value === "") {
      setResolvedMentions([]);
    }
  }, [value]);

  const clearResolved = useCallback(() => {
    setResolvedMentions([]);
  }, []);

  return {
    isOpen,
    close,
    onKeyDown,
    onInputChange,
    query: mentionQuery,
    dropdownPos: mentionDropdownPos,
    resolvedMentions,
    editingMentionIndex,
    editingMentionRef,
    handleContactSelect,
    handleHandleSelect,
    handleEmailSelect,
    handleNoMatches,
    overlaySegments,
    hasQueryOverlay,
    hasLinkOverlay,
    handleSegmentClick,
    dropdownRef,
    clearResolved,
    startPos: mentionStartPos,
  };
}
